// Pure equipment computation functions extracted from create-character-view.js

import { parseCostToCopper, slugToTitle } from './format.js';

/**
 * @typedef {import('../components/views/create-character-view.js').EquipmentPackage} EquipmentPackage
 * @typedef {import('../components/views/create-character-view.js').EquipmentItem} EquipmentItem
 */

/**
 * Compute starting gold in copper pieces from selected equipment packages.
 * @param {Array<EquipmentPackage>|undefined} classPackages
 * @param {string} classPackageId
 * @param {Array<EquipmentPackage>|undefined} bgPackages
 * @param {string} bgPackageId
 * @returns {number}
 */
export function computeStartingGoldCopper(classPackages, classPackageId, bgPackages, bgPackageId) {
  let total = 0;
  if (classPackageId && classPackages) {
    const pkg = classPackages.find((p) => p.slug === classPackageId);
    if (pkg?.gold) total += pkg.gold * 100;
    if (pkg?.silver) total += pkg.silver * 10;
    if (pkg?.copper) total += pkg.copper;
  }
  if (bgPackageId && bgPackages) {
    const pkg = bgPackages.find((p) => p.slug === bgPackageId);
    if (pkg?.gold) total += pkg.gold * 100;
    if (pkg?.silver) total += pkg.silver * 10;
    if (pkg?.copper) total += pkg.copper;
  }
  return total;
}

/**
 * Compute current gold in copper pieces after buy/sell changes.
 * For bundle-priced items, the cost covers the whole bundle so we count
 * bundles (quantity / bundleSize) rather than individual units.
 * @param {number} startingGold - Starting gold in copper
 * @param {{soldItems: Array<{slug: string, cost?: string, quantity: number}>, purchasedItems: Array<{slug: string, cost?: string, quantity: number}>}} equipmentChanges
 * @param {Array<EquipmentItem>} equipmentCatalog
 * @returns {number}
 */
export function computeCurrentGoldCopper(startingGold, equipmentChanges, equipmentCatalog) {
  /** @type {{[slug: string]: EquipmentItem}} */
  const catalog = {};
  for (const eq of equipmentCatalog) catalog[eq.slug] = eq;

  let total = startingGold;
  for (const item of equipmentChanges.soldItems) {
    const eq = catalog[item.slug];
    const bundles = eq?.bundlePricing && eq?.bundleSize ? (item.quantity || 1) / eq.bundleSize : item.quantity || 1;
    total += parseCostToCopper(item.cost || '0 cp') * bundles;
  }
  for (const item of equipmentChanges.purchasedItems) {
    const eq = catalog[item.slug];
    const bundles = eq?.bundlePricing && eq?.bundleSize ? (item.quantity || 1) / eq.bundleSize : item.quantity || 1;
    total -= parseCostToCopper(item.cost || '0 cp') * bundles;
  }
  return total;
}

/**
 * Compute the current inventory from packages + buy/sell changes.
 * @param {Array<EquipmentPackage>|undefined} classPackages
 * @param {string} classPackageId
 * @param {Array<EquipmentPackage>|undefined} bgPackages
 * @param {string} bgPackageId
 * @param {string} bgToolChoice
 * @param {{soldItems: Array<{slug: string, name?: string, cost?: string, quantity: number}>, purchasedItems: Array<{slug: string, name?: string, cost?: string, quantity: number}>}} equipmentChanges
 * @param {Array<EquipmentItem>} equipmentCatalog - Full equipment catalog for enrichment and stackability
 * @returns {Array<{slug: string, name: string, cost: string, quantity: number, category: string}>}
 */
export function computeCurrentInventory(
  classPackages,
  classPackageId,
  bgPackages,
  bgPackageId,
  bgToolChoice,
  equipmentChanges,
  equipmentCatalog,
) {
  /** @type {Set<string>} */
  const nonStackable = new Set(
    equipmentCatalog.filter((e) => e.category === 'weapon' || e.category === 'armor').map((e) => e.slug),
  );

  /** @type {{[key: string]: {slug: string, name: string, cost: string, quantity: number, category: string}}} */
  const inventory = {};
  let nextId = 0;

  const addItem = (
    /** @type {{slug: string, name: string, cost: string, quantity?: number, category: string}} */ item,
  ) => {
    const qty = item.quantity || 1;
    if (!nonStackable.has(item.slug)) {
      const existing = inventory[item.slug];
      if (existing) {
        existing.quantity += qty;
      } else {
        inventory[item.slug] = { ...item, quantity: qty };
      }
    } else {
      for (let i = 0; i < qty; i++) {
        inventory[`${item.slug}:${nextId++}`] = { ...item, quantity: 1 };
      }
    }
  };

  // Add class package items
  if (classPackageId && classPackages) {
    const pkg = classPackages.find((p) => p.slug === classPackageId);
    for (const item of pkg?.items || []) addItem(item);
  }
  // Add background package items
  if (bgPackageId && bgPackages) {
    const pkg = bgPackages.find((p) => p.slug === bgPackageId);
    for (const item of pkg?.items || []) {
      if (item.slug === '{toolChoice}') {
        if (bgToolChoice) {
          addItem({ ...item, slug: bgToolChoice, name: slugToTitle(bgToolChoice) });
        }
      } else {
        addItem(item);
      }
    }
  }
  // Apply purchased items
  for (const item of equipmentChanges.purchasedItems) {
    addItem({
      slug: item.slug,
      name: item.name || '',
      cost: item.cost || '0 cp',
      quantity: item.quantity || 1,
      category: '',
    });
  }
  // Apply sold items (remove from inventory)
  for (const item of equipmentChanges.soldItems) {
    let toRemove = item.quantity || 1;
    if (!nonStackable.has(item.slug)) {
      const existing = inventory[item.slug];
      if (existing) {
        existing.quantity -= toRemove;
        if (existing.quantity <= 0) delete inventory[item.slug];
      }
    } else {
      for (const key of Object.keys(inventory)) {
        if (toRemove <= 0) break;
        if (inventory[key]?.slug === item.slug) {
          delete inventory[key];
          toRemove--;
        }
      }
    }
  }

  // Enrich with cost from equipment data
  for (const item of Object.values(inventory)) {
    if (!item.cost) {
      const eq = equipmentCatalog.find((e) => e.slug === item.slug);
      if (eq?.cost) item.cost = eq.cost;
    }
  }

  return Object.values(inventory);
}

/**
 * Check if a character is proficient with a given equipment item.
 * @param {EquipmentItem} eq
 * @param {Array<string>} weaponProficiencies - e.g. ["Simple Weapons", "Martial Weapons"]
 * @param {Array<string>} armorTraining - e.g. ["Light Armor", "Medium Armor", "Shields"]
 * @returns {boolean | null} true if proficient, false if not, null if not applicable (adventuring gear)
 */
export function isProficient(eq, weaponProficiencies, armorTraining) {
  if (eq.weapon && eq.category === 'weapon') {
    if (weaponProficiencies.length === 0) return null;
    const cat = eq.weapon.category; // "simple" or "martial"
    const props = eq.weapon.properties || {};
    for (const prof of weaponProficiencies) {
      if (prof === 'Simple Weapons' && cat === 'simple') return true;
      if (prof === 'Martial Weapons' && cat === 'martial') return true;
      if (prof === 'Martial Finesse or Light Weapons' && cat === 'martial' && (props.finesse || props.light))
        return true;
      if (prof === 'Martial Light Weapons' && cat === 'martial' && props.light) return true;
    }
    return false;
  }
  if (eq.armor && eq.category === 'armor') {
    if (armorTraining.length === 0) return null;
    const cat = eq.armor.category; // "light", "medium", "heavy", "shield"
    for (const prof of armorTraining) {
      if (prof === 'Light Armor' && cat === 'light') return true;
      if (prof === 'Medium Armor' && cat === 'medium') return true;
      if (prof === 'Heavy Armor' && cat === 'heavy') return true;
      if (prof === 'Shields' && cat === 'shield') return true;
    }
    return false;
  }
  return null;
}

/** @type {Array<string>} */
const CATEGORY_ORDER = ['weapon', 'armor', 'adventuring-gear'];

/**
 * Group equipment by category, optionally filtered by search query.
 * @param {Array<EquipmentItem>} equipment
 * @param {string} [searchQuery='']
 * @returns {{[category: string]: Array<EquipmentItem>}}
 */
export function groupEquipmentByCategory(equipment, searchQuery = '') {
  const query = searchQuery.toLowerCase();
  const filtered = query
    ? equipment.filter((eq) => eq.name?.toLowerCase().includes(query) || eq.category?.toLowerCase().includes(query))
    : equipment;

  /** @type {{[category: string]: Array<EquipmentItem>}} */
  const unordered = {};
  for (const eq of filtered) {
    const cat = eq.category || 'Other';
    if (!unordered[cat]) unordered[cat] = [];
    unordered[cat].push(eq);
  }

  /** @type {{[category: string]: Array<EquipmentItem>}} */
  const grouped = {};
  for (const cat of CATEGORY_ORDER) {
    if (unordered[cat]) grouped[cat] = unordered[cat];
  }
  for (const cat of Object.keys(unordered)) {
    const items = unordered[cat];
    if (!grouped[cat] && items) grouped[cat] = items;
  }
  return grouped;
}

/**
 * Enrich equipment catalog with owned quantities from current inventory.
 * Returns the same category-grouped structure but with an `ownedQuantity` field
 * on items that are in the current inventory.
 * @param {{[category: string]: Array<EquipmentItem>}} equipmentByCategory
 * @param {Array<{slug: string, quantity: number}>} currentInventory
 * @returns {{[category: string]: Array<EquipmentItem & {ownedQuantity: number}>}}
 */
export function enrichEquipmentWithInventory(equipmentByCategory, currentInventory) {
  /** @type {{[slug: string]: number}} */
  const inventoryMap = {};
  for (const item of currentInventory) {
    inventoryMap[item.slug] = (inventoryMap[item.slug] || 0) + item.quantity;
  }

  /** @type {{[category: string]: Array<EquipmentItem & {ownedQuantity: number}>}} */
  const result = {};
  for (const [category, items] of Object.entries(equipmentByCategory)) {
    result[category] = items.map((eq) => ({
      ...eq,
      ownedQuantity: inventoryMap[eq.slug] || 0,
    }));
  }
  return result;
}
